import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useClosePromptDialog, usePromptDialogState } from "@/hooks/dialogs";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AutosizeTextarea } from "./ui/autosize-textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger, HoverTip } from "@/components/ui/tooltip";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { ExternalLinkIcon, Loader2Icon, XIcon } from "lucide-react";
import { Link, useLocation } from "wouter";


const reasonTruncateLength = 150;

export default function PromptDialog() {
    const inputRef = useRef<any>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const dialogState = usePromptDialogState();
    const closeDialog = useClosePromptDialog();
    const setLocation = useLocation()[1];
    const [isChecked, setIsChecked] = useState(false);
    const [inputText, setInputText] = useState('');
    const [isTemplateListOpen, setTemplateListOpen] = useState(false);
    //the list has to be portaled into the dialog, or its scroll lock cancels the
    //wheel events and the template list cannot be scrolled
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
    const hasText = !!inputText.length;

    useEffect(() => {
        if (dialogState.isOpen) {
            setIsChecked(false);
            setInputText('');
            setTemplateListOpen(false);
            setPortalContainer(anchorRef.current?.closest('[role="dialog"]') as HTMLElement ?? null);
        }
    }, [dialogState.isOpen]);

    //The field is uncontrolled, so writing to it has to go through the native setter
    //for React to pick the change up and the textarea to resize.
    const setFieldValue = (value: string) => {
        const field: HTMLInputElement | HTMLTextAreaElement | undefined =
            inputRef.current?.textArea ?? inputRef.current;
        if (!field) return;
        const proto = field instanceof HTMLTextAreaElement
            ? HTMLTextAreaElement.prototype
            : HTMLInputElement.prototype;
        Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(field, value);
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.focus();
        setInputText(value);
    };
    const clearInput = () => setFieldValue('');

    const handleSubmit = () => {
        if (!dialogState.isOpen) return;
        closeDialog();
        const input = inputRef.current?.value ?? inputRef.current?.textArea.value ?? '';
        dialogState.onSubmit(input.trim(), isChecked);
    }

    const handleForm = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        handleSubmit();
    }

    //Templates
    const templatesCfg = dialogState.templates;
    const visibleTemplates = useMemo(() => {
        if (!templatesCfg?.items) return;
        const needle = inputText.trim().toLowerCase();
        if (!needle) return templatesCfg.items;
        return templatesCfg.items.filter((tpl) => tpl.reason.toLowerCase().includes(needle));
    }, [templatesCfg, inputText]);

    const handleTemplateSelect = (template: { reason: string, flagged?: boolean }) => {
        setFieldValue(template.reason);
        if (dialogState.checkbox && !dialogState.checkbox.disabled) {
            setIsChecked(!!template.flagged);
        }
        setTemplateListOpen(false);
    };

    const handleOpenClose = (newOpenState: boolean) => {
        if (!dialogState.isOpen) return;
        if (!newOpenState) {
            closeDialog();
            if (dialogState.onCancel) {
                dialogState.onCancel();
            }
        }
    }

    // Simplifying the jsx below
    const openTemplateList = () => { if (templatesCfg) setTemplateListOpen(true); };
    const inputField = (
        <div className="relative" ref={anchorRef}>
            {dialogState.isMultiline ? (
                <AutosizeTextarea
                    autoFocus
                    ref={inputRef}
                    placeholder={dialogState.placeholder}
                    autoComplete="off"
                    required={dialogState.required}
                    minHeight={100}
                    maxHeight={240}
                    className="pr-9"
                    onClick={openTemplateList}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' && isTemplateListOpen) {
                            e.stopPropagation();
                            setTemplateListOpen(false);
                        }
                    }}

                    //Remove line breaks from the input value
                    onChange={(e) => {
                        e.target.value = e.target.value.replace(/\n\s*/g, ' ').trimStart();
                        setInputText(e.target.value);
                        openTemplateList();
                    }}
                />
            ) : (
                <Input
                    autoFocus
                    ref={inputRef}
                    placeholder={dialogState.placeholder}
                    autoComplete="off"
                    required={dialogState.required}
                    className="pr-9"
                    onClick={openTemplateList}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' && isTemplateListOpen) {
                            e.stopPropagation();
                            setTemplateListOpen(false);
                        }
                    }}
                    onChange={(e) => {
                        setInputText(e.target.value);
                        openTemplateList();
                    }}
                />
            )}
            {hasText && (
                <HoverTip label="Clear">
                    <button
                        type="button"
                        aria-label="Clear"
                        onClick={clearInput}
                        className={cn(
                            'absolute right-[13px] grid place-items-center size-5',
                            'text-faint transition-colors hover:text-accent',
                            dialogState.isMultiline ? 'top-2.5' : 'top-1/2 -translate-y-1/2',
                        )}
                    >
                        <XIcon className="size-5" />
                    </button>
                </HoverTip>
            )}
        </div>
    );

    let inputNode: React.ReactNode = inputField;
    if (templatesCfg) {
        let templatesContentNode: React.ReactNode;
        if (!templatesCfg.items || !visibleTemplates) {
            templatesContentNode = (
                <div className="text-secondary-foreground text-center p-4">
                    <Loader2Icon className="inline animate-spin size-6" />
                </div>
            );
        } else if (!templatesCfg.items.length) {
            templatesContentNode = (
                <div className="text-warning-inline text-center p-4">
                    You do not have any template configured. <br />
                    <Link
                        href={templatesCfg.addPath}
                        className="cursor-pointer underline hover:text-accent"
                        onClick={() => closeDialog()}
                    >
                        {templatesCfg.addLabel}
                        <ExternalLinkIcon className="inline mr-1 h-4" />
                    </Link>
                </div>
            );
        } else if (!visibleTemplates.length) {
            templatesContentNode = (
                <div className="text-muted-foreground text-center text-sm p-3">
                    No template matches what you are typing.
                </div>
            );
        } else {
            templatesContentNode = <>
                {visibleTemplates.map((template) => {
                    const reason = template.reason.length > reasonTruncateLength
                        ? template.reason.slice(0, reasonTruncateLength - 3) + '...'
                        : template.reason;
                    return (
                        <button
                            key={template.id}
                            type="button"
                            onClick={() => handleTemplateSelect(template)}
                            className={cn(
                                'w-full text-left rounded-bar px-2 py-1.5 text-sm',
                                'hover:bg-secondary hover:text-secondary-foreground',
                            )}
                        >
                            {template.flagged && templatesCfg.flagBadge ? (
                                <span className={cn(
                                    'inline-block pr-1.5 font-[monospace] font-medium',
                                    templatesCfg.flagClass,
                                )}>{templatesCfg.flagBadge}</span>
                            ) : null}
                            {reason}
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={() => { closeDialog(); setLocation(templatesCfg.addPath); }}
                    className="w-full text-left rounded-bar px-2 py-1.5 text-sm font-bold text-warning-inline hover:bg-secondary"
                >
                    {templatesCfg.addLabel}
                    <ExternalLinkIcon className="inline ml-1 h-4" />
                </button>
            </>;
        }
        inputNode = (
            <Popover open={isTemplateListOpen} onOpenChange={setTemplateListOpen}>
                <PopoverAnchor asChild>{inputField}</PopoverAnchor>
                <PopoverContent
                    align="start"
                    sideOffset={4}
                    collisionPadding={0}
                    container={portalContainer}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    className={cn(
                        'p-1 w-[var(--radix-popover-trigger-width)] tracking-wide',
                        'max-h-[min(18rem,var(--radix-popover-content-available-height))]',
                        'overflow-y-auto overscroll-contain',
                    )}
                >
                    {templatesContentNode}
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <Dialog open={dialogState.isOpen} onOpenChange={handleOpenClose}>
            <DialogContent className={cn(dialogState.isWide && "md:max-w-2xl")}>
                <form onSubmit={handleForm} className="grid gap-4">
                    <DialogHeader>
                        <DialogTitle>{dialogState.title}</DialogTitle>
                        <DialogDescription>
                            {dialogState.message}
                        </DialogDescription>
                    </DialogHeader>

                    {inputNode}
                    <DialogFooter className="gap-2 flex-col">
                        <div className="flex flex-col sm:flex-row sm:justify-start gap-2 w-full flex-wrap">
                            {dialogState.checkbox ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span
                                            tabIndex={dialogState.checkbox.disabled ? 0 : -1}
                                            className={cn(
                                                'inline-flex',
                                                dialogState.checkbox.disabled && 'cursor-not-allowed',
                                            )}
                                        >
                                            <label className={cn(
                                                'flex items-center gap-2 text-sm select-none',
                                                dialogState.checkbox.disabled
                                                    ? 'opacity-40 pointer-events-none'
                                                    : 'cursor-pointer',
                                            )}>
                                                <Checkbox
                                                    checked={isChecked}
                                                    onCheckedChange={(checked) => setIsChecked(checked === true)}
                                                    disabled={dialogState.checkbox.disabled}
                                                />
                                                {dialogState.checkbox.label}
                                            </label>
                                        </span>
                                    </TooltipTrigger>
                                    {dialogState.checkbox.disabled && dialogState.checkbox.disabledTooltip ? (
                                        <TooltipContent className={cn('text-center', dialogState.checkbox.tooltipClass)}>
                                            {dialogState.checkbox.disabledTooltip}
                                        </TooltipContent>
                                    ) : null}
                                </Tooltip>
                            ) : null}
                            {dialogState.suggestions && dialogState.suggestions.map((suggestion, index) => (
                                <Button
                                    key={index}
                                    type="button"
                                    onClick={() => {
                                        inputRef.current!.value = suggestion;
                                        handleSubmit();
                                    }}
                                    variant="outline"
                                >
                                    <span className="sm:hidden mr-auto text-muted-foreground">Suggestion:</span>
                                    {suggestion}
                                </Button>
                            ))}
                        </div>
                        {/* TODO: mock for kick as punishment - consider the alternative of making a "timeout" */}
                        {/* <div className="flex flex-col sm:flex-row sm:justify-start gap-2 w-full flex-wrap">
                            <div className="items-top flex space-x-2">
                                <Checkbox id="terms1" />
                                <div className="grid gap-1.5 leading-none">
                                    <label
                                        htmlFor="terms1"
                                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                        This kick is a punishment.
                                    </label>
                                    <p className="text-sm text-muted-foreground">
                                        Show in the player history as a sanction.
                                    </p>
                                </div>
                            </div>
                        </div> */}
                        <Button
                            type="submit"
                            variant={dialogState.submitBtnVariant ?? 'default'}
                        >
                            {dialogState.submitLabel ?? 'Submit'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
